import {MouseEventHandler, useContext} from "react";
import {useTranslation} from "react-i18next";
import SearchFilters from "../../common/components/SearchFilters";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {DeskDiscountGovernanceGetData} from "../../api/types/deskDiscountGovernance";

const DeskDiscountGovernanceFilters = ({
                                           handleClickResetSearch,
                                           handleGetList,
                                           testFilters,
                                           setTestFilters,
                                           responseData
                                       }: {
    handleGetList: MouseEventHandler<HTMLButtonElement>;
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>;
    testFilters: any;
    setTestFilters: any;
    responseData: DeskDiscountGovernanceGetData | undefined;
}) => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "location",
            value: testFilters.locationCodes as SelectElementModelFE,
            label: t("pricing:deskDiscountGovernance.location"),
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, locationCodes: value}),
            options: responseData?.locations.map(el => ({
                code: el.id,
                label: el.name
            })).sort((a, b) => a.label > b.label ? 1 : -1) || [],
            type: "AutocompleteM",
        },
        {
            name: "profile",
            value: testFilters.lppProfileIds as SelectElementModelFE,
            label: t("pricing:deskDiscountGovernance.profiles"),
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, lppProfileIds: value}),
            options: responseData?.profiles?.map(el => ({
                code: el.id,
                label: el.name
            })).sort((a, b) => a.label > b.label ? 1 : -1) || [],
            type: "AutocompleteM",
        },
    ];

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "active",
            value: testFilters.active as string,
            label: t("pricing:deskDiscountGovernance.enabled"),
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, active: value}),
            options: [
                {code: "true", label: t("pricing:deskDiscountGovernance.enabledYes")},
                {code: "false", label: t("pricing:deskDiscountGovernance.enabledNo")}
            ],
            type: "Autocomplete",
        },
        /*
         * F25 (LPP-21950) - T2.60b, punto 4: i due filtri agiscono sulle date di validita' della
         * configurazione, ma erano etichettati "Prenotazione dal/al" (chiave `ancillary.form.labels`,
         * condivisa con schermate dove le date sono davvero di prenotazione). Chiavi proprie della
         * governance, cosi' filtro e colonna della griglia dicono la stessa cosa.
         */
        {
            name: "from",
            value: testFilters.validityFrom as string,
            options: [],
            label: t("pricing:deskDiscountGovernance.validityFrom"),
            handleChange: (value: string) =>
                setTestFilters({...testFilters, validityFrom: value}),
            type: "Date",
        },
        {
            name: "to",
            value: testFilters.validityTo as string,
            options: [],
            label: t("pricing:deskDiscountGovernance.validityTo"),
            handleChange: (value: string) =>
                setTestFilters({...testFilters, validityTo: value}),
            type: "Date",
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: t("pricing:upsellingRate.filter.labels.legalEntity"),
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, legalEntities: value}),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: t("pricing:upsellingRate.filter.labels.dealer"),
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1
        },
    ];

    return (
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleGetList}
            handleClickResetSearch={handleClickResetSearch}
        />
    );
};

export default DeskDiscountGovernanceFilters;
